/**
 * Event Normalizer Utilities
 *
 * Transforms server events from various formats (CLI Dashboard, Vibe Remote)
 * into the unified normalized format defined in socketAdapter.ts.
 *
 * Part of Phase 2: Shared Interface & Adapter Pattern
 */

import type {
  NormalizedMessage,
  NormalizedSession,
  StreamChunk,
  MessageRole,
  SessionStatus,
  RawMessageNew,
  RawStreamChunk,
  RawSession,
  RawHistory,
} from '../types/socketAdapter';

// ============================================
// Message Normalization
// ============================================

/**
 * Normalize a raw message:new event into unified format.
 * Handles both CLI Dashboard and Vibe Remote formats.
 *
 * @param raw - Raw message event from server
 * @returns Normalized message or null if invalid
 */
export function normalizeMessage(raw: RawMessageNew): NormalizedMessage | null {
  // Extract from CLI Dashboard nested format
  const nested = raw.message;

  // Determine message ID
  const messageId =
    raw.messageId ||
    nested?.id ||
    nested?.message_id ||
    generateMessageId();

  // Determine session ID
  const sessionId = raw.sessionId || nested?.session_id;
  if (!sessionId) {
    console.warn('[EventNormalizer] Message missing sessionId:', raw);
    return null;
  }

  // Determine role (normalize 'sender' to role)
  const rawRole = raw.role || nested?.role || nested?.sender;
  const role = normalizeRole(rawRole);

  // Determine content
  const content = raw.content || nested?.content || '';

  // Determine timestamp
  const createdAt =
    raw.createdAt ||
    nested?.created_at ||
    new Date().toISOString();

  return {
    messageId,
    sessionId,
    role,
    content,
    createdAt,
    isStreaming: false,
    encrypted: false,
  };
}

/**
 * Normalize a raw stream chunk into unified format.
 *
 * @param raw - Raw stream chunk from server
 * @returns Normalized stream chunk or null if invalid
 */
export function normalizeStreamChunk(raw: RawStreamChunk): StreamChunk | null {
  // Determine session ID (CLI Dashboard uses snake_case)
  const sessionId = raw.sessionId || raw.session_id;
  if (!sessionId) {
    console.warn('[EventNormalizer] Stream chunk missing sessionId:', raw);
    return null;
  }

  // Determine message ID
  const messageId =
    raw.messageId ||
    raw.message_id ||
    generateMessageId();

  // Determine delta content (CLI Dashboard uses 'content')
  const delta = raw.delta || raw.content || '';

  // Determine role (default to assistant for streaming)
  const role = normalizeRole(raw.role) || 'assistant';

  return {
    sessionId,
    messageId,
    delta,
    role,
    isFirst: raw.isFirst ?? false,
    timestamp: raw.timestamp || Date.now(),
  };
}

/**
 * Normalize role string to MessageRole type.
 *
 * @param rawRole - Raw role string from server
 * @returns Normalized role
 */
export function normalizeRole(rawRole?: string): MessageRole {
  if (!rawRole) return 'assistant';

  const lower = rawRole.toLowerCase();

  // Handle 'sender' field values from CLI Dashboard
  if (lower === 'user' || lower === 'human') return 'user';
  if (lower === 'assistant' || lower === 'ai' || lower === 'claude') return 'assistant';
  if (lower === 'system') return 'system';

  return 'assistant';
}

// ============================================
// Session Normalization
// ============================================

/**
 * Normalize a raw session event into unified format.
 *
 * @param raw - Raw session from server
 * @returns Normalized session or null if invalid
 */
export function normalizeSession(raw: RawSession): NormalizedSession | null {
  // Determine session ID (multiple possible field names)
  const sessionId = raw.sessionId || raw.session_id || raw.id;
  if (!sessionId) {
    console.warn('[EventNormalizer] Session missing sessionId:', raw);
    return null;
  }

  // Determine status
  const rawStatus = raw.status || raw.state;
  const status = normalizeSessionStatus(rawStatus);

  // Determine title
  const title = raw.title || raw.session_name;

  // Determine timestamps (handle both Date objects and ISO strings)
  const createdAt =
    raw.createdAt ||
    raw.created_at ||
    (raw.timestamp
      ? typeof raw.timestamp === 'string'
        ? raw.timestamp
        : raw.timestamp.toISOString()
      : new Date().toISOString());

  const lastActivityAt =
    raw.lastActivityAt ||
    raw.last_activity_at ||
    createdAt;

  // Determine counts
  const messageCount = raw.messageCount ?? raw.message_count ?? 0;
  const unreadCount = raw.unreadCount ?? raw.unread_count ?? 0;

  return {
    sessionId,
    status,
    title,
    createdAt,
    lastActivityAt,
    messageCount,
    unreadCount,
    error: raw.error,
  };
}

/**
 * Normalize session status string.
 *
 * @param rawStatus - Raw status from server
 * @returns Normalized session status
 */
export function normalizeSessionStatus(rawStatus?: string): SessionStatus {
  if (!rawStatus) return 'idle';

  const lower = rawStatus.toLowerCase();

  if (lower === 'active' || lower === 'running') return 'active';
  if (lower === 'idle' || lower === 'waiting') return 'idle';
  if (lower === 'paused' || lower === 'suspended') return 'paused';
  if (lower === 'terminated' || lower === 'stopped' || lower === 'ended') return 'terminated';
  if (lower === 'crashed' || lower === 'error' || lower === 'failed') return 'crashed';

  return 'idle';
}

/**
 * Normalize a list of sessions.
 *
 * @param rawSessions - Array of raw sessions
 * @returns Array of normalized sessions (invalid ones filtered out)
 */
export function normalizeSessions(rawSessions: RawSession[]): NormalizedSession[] {
  return rawSessions
    .map(normalizeSession)
    .filter((s): s is NormalizedSession => s !== null);
}

// ============================================
// History Normalization
// ============================================

/**
 * Normalize a messages:history event.
 *
 * @param raw - Raw history event from server
 * @returns Object with sessionId and normalized messages
 */
export function normalizeHistory(raw: RawHistory): {
  sessionId: string;
  messages: NormalizedMessage[];
} | null {
  const sessionId = raw.sessionId || raw.session_id;
  if (!sessionId) {
    console.warn('[EventNormalizer] History missing sessionId:', raw);
    return null;
  }

  const messages = raw.messages
    .map((msg) => {
      const messageId =
        msg.messageId ||
        msg.message_id ||
        msg.id ||
        generateMessageId();

      const role = normalizeRole(msg.role || msg.sender);
      const content = msg.content || '';
      const createdAt = msg.createdAt || msg.created_at || new Date().toISOString();

      return {
        messageId,
        sessionId,
        role,
        content,
        createdAt,
        isStreaming: false,
        encrypted: false,
      } as NormalizedMessage;
    })
    .filter((m) => m.messageId && m.content);

  return { sessionId, messages };
}

// ============================================
// ID Generation
// ============================================

/**
 * Generate a unique message ID.
 * Uses timestamp + random component for uniqueness.
 */
export function generateMessageId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `msg_${timestamp}_${random}`;
}

/**
 * Generate a unique session ID.
 */
export function generateSessionId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `sess_${timestamp}_${random}`;
}

// ============================================
// Validation Utilities
// ============================================

/**
 * Check if a normalized message is valid for display.
 *
 * @param message - Normalized message to validate
 * @returns true if message is valid
 */
export function isValidMessage(message: NormalizedMessage): boolean {
  return !!(
    message.messageId &&
    message.sessionId &&
    message.role &&
    typeof message.content === 'string'
  );
}

/**
 * Check if a normalized session is valid.
 *
 * @param session - Normalized session to validate
 * @returns true if session is valid
 */
export function isValidSession(session: NormalizedSession): boolean {
  return !!(
    session.sessionId &&
    session.status &&
    session.createdAt
  );
}

// ============================================
// Content Transformation
// ============================================

/**
 * Sanitize message content for display.
 * Removes potentially harmful content while preserving formatting.
 *
 * @param content - Raw message content
 * @returns Sanitized content
 */
export function sanitizeContent(content: string): string {
  // Remove null bytes
  let sanitized = content.replace(/\0/g, '');

  // Normalize line endings
  sanitized = sanitized.replace(/\r\n/g, '\n');

  // Trim excessive whitespace while preserving intentional formatting
  sanitized = sanitized.replace(/\n{4,}/g, '\n\n\n');

  return sanitized;
}

/**
 * Extract a preview from message content.
 *
 * @param content - Full message content
 * @param maxLength - Maximum preview length (default: 100)
 * @returns Preview string
 */
export function extractPreview(content: string, maxLength = 100): string {
  if (content.length <= maxLength) return content;

  // Try to break at word boundary
  const truncated = content.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.7) {
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
}
